import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import io from 'socket.io-client';

import { BrowserPushNotificationService } from '@services/Logic/browser-push-notification.service';
import { MessageService } from './message.service';
import { StorageService } from '../Logic/storage.service';
import { StateService } from '../Logic/state.service';
import { environment } from '../../../environments/environment';
import { Negotiation } from '@app/models/negotiation.model';
import { Video } from '@app/models/video/video.model';

@Injectable()
export class SocketService {
  baseUrl = environment.socketUrl;
  private socket: any;

  public messageReceivedSource: Subject<any> = new Subject<any>();
  public messageReceived$: Observable<any> =
    this.messageReceivedSource.asObservable();

  public messageUpdatedSource: Subject<any> = new Subject<any>();

  public taskCommentCensored: Subject<number> = new Subject<number>();
  public offerCommentCensored: Subject<number> = new Subject<number>();
  public offerMessageCensored: Subject<number> = new Subject<number>();

  public readAllEventSource: Subject<any> = new Subject<any>();
  public readAllEvent$: Observable<any> =
    this.readAllEventSource.asObservable();

  public notificationReceivedSource: Subject<any> = new Subject<any>();
  public notificationReceived$: Observable<any> =
    this.notificationReceivedSource.asObservable();

  public videoProcessedSource = new Subject<Video>();
  public videoProcessed$ = this.videoProcessedSource.asObservable();

  constructor(
    private browserPushNotificationService: BrowserPushNotificationService,
    private messageService: MessageService,
    private router: Router,
    private stateService: StateService,
    private storage: StorageService,
  ) {}

  connectSocket() {
    if (!this.socket || (this.socket && !this.socket.connected)) {
      this.socket = io(this.baseUrl, {
        query: { token: this.storage.authToken },
        transports: ['websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity,
      });

      this.socket.connect();

      setTimeout(() => {
        this.storage.socketId = this.socket.id;
        this.listenToEvents();
      }, 1500);
    }
  }

  disconnectSocket() {
    if (this.socket && this.socket.connected) {
      this.socket.disconnect();
    }
  }

  listenToEvents() {
    // Socket is reconnecting
    this.socket.on('new_socket', (data: any) => {
      this.storage.socketId = data.id;
    });

    // start listening for message events
    this.socket.on('message-created', (data: any) => {
      this.messageService.getNewMessagesCount().subscribe((res) => {
        this.stateService.showNewMessagesHeader(res);
      });
      this.messageReceivedSource.next(data);
    });
    this.socket.on('message-updated', (data: any) => {
      this.messageService.getNewMessagesCount().subscribe((res) => {
        this.stateService.showNewMessagesHeader(res);
      });
      this.messageUpdatedSource.next(data);
    });

    // start listening for readAll events
    this.socket.on('readAll', (data: any) => {
      // if (this.router.url === '/beskeder' - should read all when entering messages?
      this.readAllEventSource.next(data);
    });

    // start listening for notification events
    this.socket.on('notification-created', (data: any) => {
      this.stateService.showNewNotificationsHeader(data);
      this.stateService.addNotificationToSidebar(data);
      this.browserPushNotificationService.createNotification(
        data.title,
        data.message,
        data.type,
        () => {
          if (data.convId) {
            return this.router.navigateByUrl(`beskeder?id=${data.convId}`);
          }
          if (data.taskId) {
            return this.router.navigateByUrl(`/opgaver/link/${data.taskId}`);
          }
        },
      );
    });

    // listen to task_updated
    this.socket.on('task-updated', (data: any) => {
      this.stateService.showUpdatedTask(data[1].taskId);
    });

    // listen to new task
    // for browser push notifications enabled
    this.socket.on('task-created', (data: any) => {
      this.browserPushNotificationService.createNotification(
        data[1].title,
        data[1].message,
        data[1].type,
        // eslint-disable-next-line consistent-return
        () => {
          if (data[1].id) {
            return this.router.navigateByUrl(`/opgaver/opgave/${data[1].id}`);
          }
        },
      );
    });

    // listen to new comment
    this.socket.on('task-comment-created', (data: any) => {
      this.stateService.showUpdatedComment(data[1]);
    });

    // listen to new offers
    this.socket.on('offer-created', (data: any) => {
      this.stateService.showNewOffer(data[1]);
    });

    // listen to new offer comment
    this.socket.on('offer-comment-created', (data: any) => {
      this.stateService.showUpdatedOfferComment(data[1]);
    });

    this.socket.on('censored', (data: any) => {
      const { id, type } = data;
      switch (type) {
        case 'task_comment':
          this.taskCommentCensored.next(id);
          break;
        case 'offer_comment':
          this.offerCommentCensored.next(id);
          break;
        case 'offer_message':
          this.offerMessageCensored.next(id);
          break;
        default:
          break;
      }
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.socket.on('task-completed', ([_, task]) => {
      this.stateService.showUpdatedTask(task.id);
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.socket.on('task-cancellation-completed', ([_, task]) => {
      this.stateService.showUpdatedTask(task.id);
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.socket.on('task-cancellation-requested', ([_, task]) => {
      this.stateService.showUpdatedTask(task.id);
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.socket.on('task-cancellation-undone', ([_, task]) => {
      this.stateService.showUpdatedTask(task.id);
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.socket.on('offer-accepted', ([_, offer]) => {
      this.stateService.showUpdatedTask(offer.taskId);
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.socket.on('offer-updated', ([_, offer]) => {
      this.stateService.showUpdatedOffer(offer.id);
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.socket.on('offer-cancelled', ([_, offer]) => {
      this.stateService.showCancelledOffer(offer.id);
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.socket.on('offer-pi-requested', ([_, offerPriceIncrease]) => {
      this.stateService.showUpdatedTask(offerPriceIncrease.taskId);
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.socket.on('offer-pi-accepted', ([_, offerPriceIncrease]) => {
      this.stateService.showUpdatedTask(offerPriceIncrease.taskId);
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.socket.on('offer-pi-cancelled', ([_, offerPriceIncrease]) => {
      this.stateService.showUpdatedTask(offerPriceIncrease.taskId);
    });

    this.socket.on(
      'negotiation-created',
      ([_, negotiation]: [string, Negotiation]) => {
        this.stateService.createOrUpdateNegotiation(negotiation);
      },
    );

    this.socket.on(
      'negotiation-updated',
      ([_, negotiation]: [string, Negotiation]) => {
        this.stateService.createOrUpdateNegotiation(negotiation);
      },
    );

    this.socket.on('support-ticket-updated', (supportTicket) => {
      this.stateService.supportTicketUpdate(supportTicket);
    });

    this.socket.on('video-processing-completed', ([_, data]: [string, Video]) => {
      this.videoProcessedSource.next(data);
    });
  }
}
